import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Grid,
  Card,
  CardMedia,
  CardActions,
  Typography,
  CircularProgress,
  Chip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon
} from '@material-ui/icons';
import { uploadCompressedVehicleImage, deleteVehicleImage } from '../services/imageUploadService';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  uploadButton: {
    marginBottom: theme.spacing(2)
  },
  input: {
    display: 'none'
  },
  imageCard: {
    position: 'relative',
    height: 200,
    '&:hover $imageOverlay': {
      opacity: 1
    }
  },
  imageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s'
  },
  mainBadge: {
    position: 'absolute',
    top: theme.spacing(1),
    left: theme.spacing(1),
    zIndex: 1
  },
  placeholder: {
    height: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[200],
    border: `2px dashed ${theme.palette.grey[400]}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.grey[300]
    }
  },
  uploadProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -20,
    marginLeft: -20
  }
}));

/**
 * Composant pour uploader et gérer les images de véhicules
 * @param {string} vehicleId - ID du véhicule
 * @param {Array<string>} images - Liste des URLs des images existantes
 * @param {Function} onChange - Callback appelé quand les images changent
 * @param {number} maxImages - Nombre maximum d'images (défaut: 5)
 */
const VehicleImageUpload = ({ vehicleId, images = [], onChange, maxImages = 5 }) => {
  const classes = useStyles();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    // Vérifier le nombre maximum d'images
    if (images.length + files.length > maxImages) {
      alert(`Vous ne pouvez uploader que ${maxImages} images maximum`);
      return;
    }

    // Vérifier que vehicleId existe
    if (!vehicleId || vehicleId === 'temp') {
      alert('Veuillez d\'abord sauvegarder le véhicule avant d\'ajouter des images');
      return;
    }

    setUploading(true);

    try {
      const uploadedUrls = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const imageType = images.length + i === 0 ? 'main' : `image_${images.length + i}`;
        
        setUploadProgress(prev => ({ ...prev, [i]: 0 }));

        try {
          const url = await uploadCompressedVehicleImage(file, vehicleId, imageType);
          uploadedUrls.push(url);
          setUploadProgress(prev => ({ ...prev, [i]: 100 }));
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          alert(`Erreur lors de l'upload de ${file.name}: ${error.message}`);
        }
      }

      // Mettre à jour la liste des images
      const newImages = [...images, ...uploadedUrls];
      onChange(newImages);

    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Erreur lors de l\'upload des images');
    } finally {
      setUploading(false);
      setUploadProgress({});
      // Reset input
      event.target.value = '';
    }
  };

  const handleDelete = async (imageUrl, index) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      return;
    }

    try {
      await deleteVehicleImage(imageUrl);
      
      // Mettre à jour la liste des images
      const newImages = images.filter((_, i) => i !== index);
      onChange(newImages);
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Erreur lors de la suppression de l\'image');
    }
  };

  const handleSetMain = (index) => {
    // Déplacer l'image sélectionnée en première position
    const newImages = [...images];
    const [selectedImage] = newImages.splice(index, 1);
    newImages.unshift(selectedImage);
    onChange(newImages);
  };

  return (
    <Box className={classes.root}>
      <Typography variant="h6" gutterBottom>
        Images du véhicule
      </Typography>
      
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Ajoutez jusqu'à {maxImages} images. La première image sera l'image principale.
      </Typography>

      {/* Bouton d'upload */}
      <input
        accept="image/jpeg,image/jpg,image/png,image/webp"
        className={classes.input}
        id="vehicle-image-upload"
        multiple
        type="file"
        onChange={handleFileSelect}
        disabled={uploading || images.length >= maxImages}
      />
      <label htmlFor="vehicle-image-upload">
        <Button
          variant="contained"
          color="primary"
          component="span"
          startIcon={<UploadIcon />}
          className={classes.uploadButton}
          disabled={uploading || images.length >= maxImages}
        >
          {uploading ? 'Upload en cours...' : 'Ajouter des images'}
        </Button>
      </label>

      {images.length >= maxImages && (
        <Typography variant="caption" color="error" display="block" gutterBottom>
          Nombre maximum d'images atteint ({maxImages})
        </Typography>
      )}

      {/* Grille d'images */}
      <Grid container spacing={2}>
        {images.map((imageUrl, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card className={classes.imageCard}>
              {index === 0 && (
                <Chip
                  label="Image principale"
                  color="primary"
                  size="small"
                  className={classes.mainBadge}
                />
              )}
              
              <CardMedia
                component="img"
                height="200"
                image={imageUrl}
                alt={`Véhicule ${index + 1}`}
              />
              
              <Box className={classes.imageOverlay}>
                <CardActions>
                  {index !== 0 && (
                    <Button
                      size="small"
                      color="primary"
                      variant="contained"
                      onClick={() => handleSetMain(index)}
                    >
                      Définir comme principale
                    </Button>
                  )}
                  <IconButton
                    color="secondary"
                    onClick={() => handleDelete(imageUrl, index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Box>
            </Card>
          </Grid>
        ))}

        {/* Placeholder pour ajouter des images */}
        {images.length < maxImages && !uploading && (
          <Grid item xs={12} sm={6} md={4}>
            <label htmlFor="vehicle-image-upload">
              <Box className={classes.placeholder}>
                <Box textAlign="center">
                  <ImageIcon style={{ fontSize: 48, color: '#999' }} />
                  <Typography variant="body2" color="textSecondary">
                    Cliquez pour ajouter
                  </Typography>
                </Box>
              </Box>
            </label>
          </Grid>
        )}

        {/* Indicateur de progression */}
        {uploading && (
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.placeholder}>
              <CircularProgress className={classes.uploadProgress} />
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Informations */}
      <Box mt={2}>
        <Typography variant="caption" color="textSecondary">
          • Formats acceptés: JPG, PNG, WebP<br />
          • Taille maximale: 5 MB par image<br />
          • Les images sont automatiquement compressées
        </Typography>
      </Box>
    </Box>
  );
};

export default VehicleImageUpload;
